import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import React from "react";
import { classNames } from "../../../helpers";

/** Shared building blocks for the schedule drawers (same look as the class drawer / onboarding). */

export function Icon({ d, className = "h-5 w-5" }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      {d}
    </svg>
  );
}

export const inputClass = (hasError) =>
  classNames(
    "block w-full rounded-2xl border bg-white px-4 py-3 text-base text-gray-900 placeholder:text-gray-400 outline-none transition focus:ring-4",
    hasError ? "border-red-300 focus:border-red-500 focus:ring-red-100" : "border-gray-200 focus:border-indigo-bz focus:ring-[#F3E9FF]"
  );

export function FieldLabel({ htmlFor, children, optional }) {
  return (
    <label htmlFor={htmlFor} className="mb-1.5 flex items-center gap-2 text-sm font-extrabold text-gray-900">
      {children}
      {optional && <span className="text-xs font-semibold text-gray-400">Optional</span>}
    </label>
  );
}

export function FieldError({ children }) {
  if (!children) return null;
  return (
    <p className="mt-1.5 text-sm font-semibold text-red-600" role="alert">
      {children}
    </p>
  );
}

export function Chip({ active, onClick, children, className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={classNames(
        "rounded-full border px-3.5 py-1.5 text-sm font-bold transition-colors",
        active ? "border-indigo-bz bg-[#F3E9FF] text-indigo-bz" : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50",
        className
      )}
    >
      {children}
    </button>
  );
}

export function Card({ title, children, className = "" }) {
  return (
    <section className={classNames("rounded-3xl border border-gray-200 bg-white p-4 shadow-[0_10px_30px_rgba(72,27,58,0.05)] sm:p-5", className)}>
      {title && <h3 className="mb-4 text-base font-black text-gray-900">{title}</h3>}
      {children}
    </section>
  );
}

/** Right-side drawer: eyebrow + title header, scrolling body, Cancel / primary footer. */
export function FcDrawer({ open, onClose, eyebrow, title, subtitle, children, primaryLabel, onPrimary, busy = false, primaryDisabled = false, footerNote = null, wide = false, hideFooter = false }) {
  return (
    <Dialog open={open} onClose={() => !busy && onClose()} className="relative z-50">
      <DialogBackdrop transition className="fixed inset-0 bg-[#1d1320]/45 transition-opacity duration-300 ease-out data-[closed]:opacity-0" />
      <div className="fixed inset-0 overflow-hidden">
        <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full sm:pl-10">
          <DialogPanel transition className={classNames("pointer-events-auto flex h-full w-screen transform flex-col bg-[#F7F4F8] shadow-2xl transition duration-300 ease-out data-[closed]:translate-x-full sm:rounded-l-3xl", wide ? "max-w-3xl" : "max-w-xl")}>
            <div className="flex items-start justify-between gap-4 border-b border-gray-200 bg-white px-5 py-4 sm:rounded-tl-3xl sm:px-7 sm:py-5">
              <div className="min-w-0">
                {eyebrow && <p className="text-xs font-extrabold tracking-wide text-portland-orange">{eyebrow}</p>}
                <DialogTitle className="mt-0.5 truncate text-xl font-black tracking-tight text-gray-900 sm:text-2xl">{title}</DialogTitle>
                {subtitle && <p className="mt-1 text-sm text-gray-600">{subtitle}</p>}
              </div>
              <button type="button" onClick={() => !busy && onClose()} aria-label="Close" className="-mr-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 hover:text-gray-900">
                <Icon d={<path d="M18 6 6 18M6 6l12 12" />} />
              </button>
            </div>
            {/* [&>*]:shrink-0: blocks keep their natural height and the panel scrolls. Without it, blocks that clip their
                content (overflow-hidden / overflow-x-auto, e.g. a long member list or a tab bar) shrank to fit the window:
                the list was cut off without scrolling and the tabs were squeezed into a thin strip. */}
            <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-5 py-6 sm:px-7 [&>*]:shrink-0">{children}</div>
            {!hideFooter && (
            <div className="flex items-center justify-between gap-3 border-t border-gray-200 bg-white px-5 py-4 sm:rounded-bl-3xl sm:px-7">
              <div className="min-w-0 text-sm text-gray-600">{footerNote}</div>
              <div className="flex shrink-0 items-center gap-2">
                <button type="button" onClick={onClose} disabled={busy} className="rounded-xl px-4 py-3 text-sm font-bold text-gray-600 hover:bg-gray-100 disabled:opacity-50">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onPrimary}
                  disabled={busy || primaryDisabled}
                  className="flex min-w-36 items-center justify-center gap-2 rounded-2xl bg-brown-coffee px-6 py-3.5 text-base font-extrabold text-white hover:bg-light-brown-coffee disabled:opacity-60"
                >
                  {busy && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                  {primaryLabel}
                </button>
              </div>
            </div>
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

/** Plain titled block for use inside FcModal (white body, no card border). */
export function Section({ title, children, className = "" }) {
  return (
    <section className={className}>
      {title && <h3 className="mb-3 text-sm font-black text-gray-900">{title}</h3>}
      {children}
    </section>
  );
}

/**
 * Centred pop-up for quick actions on a session (opens on top of the session drawer).
 * Bottom sheet on phones. Render it INSIDE the parent <Dialog> so headlessui treats it as nested:
 * clicking outside closes only the pop-up, the session drawer stays open.
 */
export function FcModal({ open, onClose, eyebrow, title, subtitle, children, primaryLabel, onPrimary, busy = false, primaryDisabled = false, footerNote = null, size = "md", danger = false }) {
  return (
    <Dialog open={open} onClose={() => !busy && onClose()} className="relative z-50">
      <DialogBackdrop transition className="fixed inset-0 bg-[#1d1320]/40 transition-opacity duration-200 data-[closed]:opacity-0" />
      <div className="fixed inset-0 flex items-end justify-center sm:items-center sm:p-4">
        <DialogPanel
          transition
          className={classNames(
            "flex max-h-[92vh] w-full flex-col rounded-t-3xl bg-white shadow-2xl transition duration-200 ease-out data-[closed]:translate-y-6 data-[closed]:opacity-0 sm:max-h-[86vh] sm:rounded-3xl",
            size === "lg" ? "sm:max-w-xl" : "sm:max-w-lg"
          )}
        >
          <div className="flex items-start justify-between gap-4 px-6 pb-3 pt-6">
            <div className="min-w-0">
              {eyebrow && <p className="text-xs font-extrabold tracking-wide text-portland-orange">{eyebrow}</p>}
              <DialogTitle className="mt-0.5 text-xl font-black tracking-tight text-gray-900">{title}</DialogTitle>
              {subtitle && <p className="mt-1 text-sm text-gray-600">{subtitle}</p>}
            </div>
            <button type="button" onClick={() => !busy && onClose()} aria-label="Close" className="-mr-2 -mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 hover:text-gray-900">
              <Icon d={<path d="M18 6 6 18M6 6l12 12" />} />
            </button>
          </div>
          <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 pb-5 pt-2 [&>*]:shrink-0">{children}</div>
          <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-6 py-4">
            <div className="min-w-0 text-xs text-gray-600 sm:text-sm">{footerNote}</div>
            <div className="flex shrink-0 items-center gap-2">
              <button type="button" onClick={onClose} disabled={busy} className="rounded-xl px-4 py-3 text-sm font-bold text-gray-600 hover:bg-gray-100 disabled:opacity-50">
                Cancel
              </button>
              <button
                type="button"
                onClick={onPrimary}
                disabled={busy || primaryDisabled}
                className={classNames("flex min-w-32 items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-extrabold text-white disabled:opacity-60", danger ? "bg-[#B4232F] hover:bg-[#961C27]" : "bg-brown-coffee hover:bg-light-brown-coffee")}
              >
                {busy && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                {primaryLabel}
              </button>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

/** Centered confirmation in the same theme. */
export function FcConfirmDialog({ open, title, message, confirmLabel = "Confirm", cancelLabel = "Keep it", danger = false, busy = false, onConfirm, onClose }) {
  return (
    <Dialog open={open} onClose={() => !busy && onClose()} className="relative z-[60]">
      <DialogBackdrop transition className="fixed inset-0 bg-[#1d1320]/45 transition-opacity duration-200 data-[closed]:opacity-0" />
      <div className="fixed inset-0 flex items-end justify-center p-0 sm:items-center sm:p-4">
        <DialogPanel transition className="w-full max-w-md rounded-t-3xl bg-white p-6 shadow-2xl transition duration-200 data-[closed]:translate-y-4 data-[closed]:opacity-0 sm:rounded-3xl">
          <div className="flex items-start gap-4">
            <span className={classNames("flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl", danger ? "bg-[#FFE8EB] text-[#B4232F]" : "bg-[#F3E9FF] text-indigo-bz")}>
              <Icon d={<><path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></>} />
            </span>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-black text-gray-900">{title}</DialogTitle>
              <p className="mt-1 text-sm text-gray-600">{message}</p>
            </div>
          </div>
          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button type="button" onClick={onClose} disabled={busy} className="rounded-2xl border border-gray-200 px-5 py-3 text-sm font-extrabold text-gray-900 hover:bg-gray-50 disabled:opacity-50">
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              className={classNames(
                "flex items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-extrabold text-white disabled:opacity-60",
                danger ? "bg-[#B4232F] hover:bg-[#961C27]" : "bg-brown-coffee hover:bg-light-brown-coffee"
              )}
            >
              {busy && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
              {confirmLabel}
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

const pad = (n) => String(n).padStart(2, "0");
export const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/**
 * Month calendar. mode "single": value is "YYYY-MM-DD"; mode "multi": value is ["YYYY-MM-DD", ...].
 * Days before minDate ("YYYY-MM-DD") are disabled.
 */
export function MiniCalendar({ mode = "single", value, onChange, minDate }) {
  const selected = mode === "multi" ? value || [] : value ? [value] : [];
  const initial = selected.length ? new Date(`${selected[0]}T12:00:00`) : new Date();
  const [month, setMonth] = React.useState(new Date(initial.getFullYear(), initial.getMonth(), 1));
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const lead = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const todayYmd = ymd(new Date());
  const cells = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(month.getFullYear(), month.getMonth(), d));
  const toggle = (key) => {
    if (mode === "multi") onChange(selected.indexOf(key) > -1 ? selected.filter((x) => x !== key) : [...selected, key].sort());
    else onChange(key);
  };
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-3">
      <div className="mb-2 flex items-center justify-between">
        <button type="button" aria-label="Previous month" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="flex h-9 w-9 items-center justify-center rounded-xl text-gray-600 hover:bg-gray-100">
          <Icon className="h-4 w-4" d={<path d="m15 18-6-6 6-6" />} />
        </button>
        <span className="text-sm font-black text-gray-900">{MONTHS[month.getMonth()]} {month.getFullYear()}</span>
        <button type="button" aria-label="Next month" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="flex h-9 w-9 items-center justify-center rounded-xl text-gray-600 hover:bg-gray-100">
          <Icon className="h-4 w-4" d={<path d="m9 18 6-6-6-6" />} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-extrabold uppercase text-gray-400">
        {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
          <span key={d} className="py-1">{d}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <span key={`e${i}`} />;
          const key = ymd(d);
          const disabled = minDate && key < minDate;
          const isSel = selected.indexOf(key) > -1;
          return (
            <button
              key={key}
              type="button"
              disabled={disabled}
              onClick={() => toggle(key)}
              aria-pressed={isSel}
              aria-label={key}
              className={classNames(
                "flex h-10 items-center justify-center rounded-xl text-sm font-extrabold transition-colors",
                disabled ? "cursor-not-allowed text-gray-300" : isSel ? "bg-brown-coffee text-white" : "text-gray-800 hover:bg-[#F3E9FF]",
                !isSel && key === todayYmd ? "ring-1 ring-inset ring-portland-orange" : ""
              )}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
